<script setup>
import { computed, ref } from 'vue'
import MayoresVisorMapa from './MayoresVisorMapa.vue'
import MayoresMapaCompleto from './MayoresMapaCompleto.vue'

const props = defineProps({
  territorio: Object,
  terrKey: String,
  workedManzanas: { type: Array, default: () => [] },
  mapaUrlDeManzana: { type: Function, required: true },
})

const emit = defineEmits(['como-llegar'])

const manzanas = computed(() => {
  const mz = props.territorio?.mzNumbers
  if (!Array.isArray(mz) || mz.length <= 1) return []
  const result = []
  for (let i = 1; i < mz.length; i++) {
    if (mz[i]?.[0]) result.push(i)
  }
  return result
})

const manzanasPendientes = computed(() => manzanas.value.filter((idx) => !trabajada(idx)))

// Todas las manzanas con coordenada, con su estado, para el mapa completo.
const manzanasParaMapa = computed(() =>
  manzanas.value.map((idx) => ({
    idx,
    latlng: props.territorio.mzNumbers[idx][0],
    trabajada: trabajada(idx),
  }))
)

// 'completo' muestra todo el territorio en un mapa; 'manzana' muestra la cuadrícula de mapas por manzana.
const vista = ref('completo')

const mapaConError = ref(new Set())

const onErrorMapa = (idx) => {
  const next = new Set(mapaConError.value)
  next.add(idx)
  mapaConError.value = next
}

const trabajada = (idx) => props.workedManzanas.includes(idx)

const puntoTerritorio = computed(() => props.territorio?.puntoEncuentro?.[0]?.latlng || null)

// ─── Visor de mapa en pantalla completa ────────────────────────────────────

const manzanaAbierta = ref(null)

const abrirManzana = (idx) => {
  manzanaAbierta.value = idx
}
</script>

<template>
  <div class="mayores-contenido">
    <button
      v-if="puntoTerritorio"
      type="button"
      class="boton-secundario"
      style="margin-bottom:var(--space-3)"
      @click="emit('como-llegar', puntoTerritorio)"
    >
      <i class="pi pi-map-marker" aria-hidden="true" /> Cómo llegar a este territorio
    </button>

    <div v-if="!manzanas.length" class="mayores-estado">
      <i class="pi pi-info-circle mayores-estado__icono" aria-hidden="true" />
      <p>Este territorio no tiene manzanas numeradas.</p>
    </div>

    <template v-else>
      <div class="mayores-vista-toggle" role="group" aria-label="Forma de ver el territorio">
        <button
          type="button"
          class="mayores-vista-toggle__boton"
          :class="{ 'mayores-vista-toggle__boton--activo': vista === 'completo' }"
          :aria-pressed="vista === 'completo'"
          @click="vista = 'completo'"
        >
          <i class="pi pi-map" aria-hidden="true" /> Mapa completo
        </button>
        <button
          type="button"
          class="mayores-vista-toggle__boton"
          :class="{ 'mayores-vista-toggle__boton--activo': vista === 'manzana' }"
          :aria-pressed="vista === 'manzana'"
          @click="vista = 'manzana'"
        >
          <i class="pi pi-th-large" aria-hidden="true" /> Por manzana
        </button>
      </div>

      <MayoresMapaCompleto
        v-if="vista === 'completo'"
        :limits="territorio.limits || []"
        :color="territorio.color || {}"
        :manzanas="manzanasParaMapa"
        :etiqueta="terrKey"
      />

      <template v-else>
        <p class="campo__etiqueta">Manzanas que faltan predicar</p>

        <div v-if="!manzanasPendientes.length" class="mayores-estado mayores-exito">
          <i class="pi pi-check-circle mayores-estado__icono" aria-hidden="true" />
          <p>¡Ya trabajaron todas las manzanas de este territorio!</p>
        </div>

        <div v-else class="mayores-mapa-grid">
          <button
            v-for="idx in manzanasPendientes"
            :key="idx"
            type="button"
            class="mayores-mapa-celda"
            :disabled="mapaConError.has(idx)"
            @click="abrirManzana(idx)"
          >
            <img
              v-if="!mapaConError.has(idx)"
              :src="mapaUrlDeManzana(idx)"
              :alt="`Mapa de la manzana ${idx} del territorio ${terrKey}`"
              class="mayores-mapa-celda__img"
              @error="onErrorMapa(idx)"
            >
            <div v-else class="mayores-mapa-celda__error">
              <i class="pi pi-image" aria-hidden="true" />
              <span>No disponible</span>
            </div>
            <span class="mayores-mapa-celda__numero">Manzana {{ idx }}</span>
          </button>
        </div>
      </template>
    </template>

    <MayoresVisorMapa
      :src="manzanaAbierta !== null ? mapaUrlDeManzana(manzanaAbierta) : null"
      :alt="`Mapa de la manzana ${manzanaAbierta} del territorio ${terrKey}`"
      :etiqueta="`Mapa de la manzana ${manzanaAbierta}`"
      @cerrar="manzanaAbierta = null"
    />
  </div>
</template>
